import { theme } from 'antd';
import { ChevronDown, LayoutGrid } from 'lucide-react';
import type { SubsystemDto } from '@api/subsystemsApi';

interface CategoryNavProps {
  acqSubsystems: SubsystemDto[];
  acqActiveCode: string | null;
  acqOnTabClick: (acqCode: string) => void;
}

/**
 * Горизонтальна смужка з top-level підсистемами як вкладками.
 * Активна вкладка підкреслена, клік на ту саму вкладку = toggle (закриває MegaMenu).
 * Висота 38px (узгоджено з ТЗ).
 */
export default function CategoryNav({ acqSubsystems, acqActiveCode, acqOnTabClick }: CategoryNavProps) {
  const { token: acqTheme } = theme.useToken();

  return (
    <div
      style={{
        height: 38,
        background: '#fff',
        borderBottom: `1px solid ${acqTheme.colorBorderSecondary}`,
        display: 'flex',
        alignItems: 'stretch',
        padding: '0 8px',
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
      role="navigation"
      aria-label="Головне меню"
    >
      {/* Дашборд (завжди першим) */}
      <NavTab
        acqIcon={<LayoutGrid size={14} />}
        acqLabel="Дашборд"
        acqActive={acqActiveCode === 'dashboard'}
        acqHasChevron={false}
        acqOnClick={() => acqOnTabClick('dashboard')}
      />
      {acqSubsystems.map((acqS) => (
        <NavTab
          key={acqS.code}
          acqLabel={acqS.name}
          acqActive={acqActiveCode === acqS.code}
          acqHasChevron={(acqS.children?.length ?? 0) + (acqS.sections?.length ?? 0) > 0}
          acqOnClick={() => acqOnTabClick(acqS.code)}
        />
      ))}
    </div>
  );
}

function NavTab({
  acqIcon,
  acqLabel,
  acqActive,
  acqHasChevron,
  acqOnClick,
}: {
  acqIcon?: JSX.Element;
  acqLabel: string;
  acqActive: boolean;
  acqHasChevron: boolean;
  acqOnClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={acqOnClick}
      aria-current={acqActive ? 'true' : undefined}
      style={{
        padding: '0 12px',
        height: 38,
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 13,
        color: acqActive ? '#534AB7' : 'inherit',
        fontWeight: acqActive ? 500 : 400,
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderBottom: acqActive ? '2px solid #534AB7' : '2px solid transparent',
        background: acqActive ? '#fff' : 'transparent',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => { if (!acqActive) e.currentTarget.style.background = '#f5f5f5'; }}
      onMouseLeave={(e) => { if (!acqActive) e.currentTarget.style.background = 'transparent'; }}
    >
      {acqIcon}
      {acqLabel}
      {acqHasChevron && <ChevronDown size={12} />}
    </button>
  );
}
